import { useChatColors } from '@/contexts/ChatColorsContext';
import type { ActionButton, ActionButtonVariant } from '@/types/chat';

interface ChatActionButtonsProps {
  buttons: ActionButton[];
  used?: boolean;
  onFlowClick: (value: string) => void;
}

const fixedVariantClasses: Partial<Record<ActionButtonVariant, string>> = {
  mercadolivre: 'bg-yellow-400 text-gray-800 hover:bg-yellow-500 border-2 border-yellow-400 font-semibold',
  shopee: 'bg-orange-500 text-white hover:bg-orange-600 border-2 border-orange-500',
  whatsapp: 'bg-green-500 text-white hover:bg-green-600 border-2 border-green-500',
  instagram: 'bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white hover:opacity-90 border-0',
};

const variantIcons: Partial<Record<ActionButtonVariant, string>> = {
  mercadolivre: 'ri-store-2-line',
  shopee: 'ri-shopping-bag-line',
  whatsapp: 'ri-whatsapp-line',
  instagram: 'ri-instagram-line',
};

export default function ChatActionButtons({ buttons, used, onFlowClick }: ChatActionButtonsProps) {
  const { primary } = useChatColors();

  return (
    <div className="flex flex-wrap gap-2 mt-2 ml-10">
      {buttons.map((btn) => {
        const variant = btn.variant ?? 'primary';
        const iconClass = variantIcons[variant];
        const baseClass = 'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md';

        if (variant !== 'primary') {
          return (
            <a
              key={btn.id}
              href={btn.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${baseClass} ${fixedVariantClasses[variant] ?? ''}`}
            >
              {iconClass && <i className={`${iconClass} text-base`} />}
              {btn.label}
            </a>
          );
        }

        // Primary flow buttons — use accent color
        return (
          <button
            key={btn.id}
            type="button"
            disabled={used}
            onClick={() => !used && onFlowClick(btn.value ?? '')}
            className={`${baseClass} bg-white border-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            style={{
              borderColor: primary,
              color: primary,
            }}
            onMouseEnter={(e) => {
              if (!used) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = primary;
                (e.currentTarget as HTMLButtonElement).style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#fff';
              (e.currentTarget as HTMLButtonElement).style.color = primary;
            }}
          >
            {btn.label}
          </button>
        );
      })}
    </div>
  );
}
